/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import { assistantsCreate } from "../funcs/assistantsCreate.js";
import { assistantsDelete } from "../funcs/assistantsDelete.js";
import { assistantsEnsureManaged } from "../funcs/assistantsEnsureManaged.js";
import { assistantsGet } from "../funcs/assistantsGet.js";
import { assistantsGetManaged } from "../funcs/assistantsGetManaged.js";
import { assistantsInterruptTurn } from "../funcs/assistantsInterruptTurn.js";
import { assistantsList } from "../funcs/assistantsList.js";
import { assistantsSendMessage } from "../funcs/assistantsSendMessage.js";
import { assistantsUpdate } from "../funcs/assistantsUpdate.js";
import { assistantsUpgradeIdentity } from "../funcs/assistantsUpgradeIdentity.js";
import { ClientSDK, RequestOptions } from "../lib/sdks.js";
import { Assistant } from "../models/components/assistant.js";
import { InterruptTurnResult } from "../models/components/interruptturnresult.js";
import { ListAssistantsResult } from "../models/components/listassistantsresult.js";
import { SendMessageResult } from "../models/components/sendmessageresult.js";
import {
  CreateAssistantRequest,
  CreateAssistantSecurity,
} from "../models/operations/createassistant.js";
import {
  DeleteAssistantRequest,
  DeleteAssistantSecurity,
} from "../models/operations/deleteassistant.js";
import {
  EnsureManagedAssistantRequest,
  EnsureManagedAssistantSecurity,
} from "../models/operations/ensuremanagedassistant.js";
import {
  GetAssistantRequest,
  GetAssistantSecurity,
} from "../models/operations/getassistant.js";
import {
  GetManagedAssistantRequest,
  GetManagedAssistantSecurity,
} from "../models/operations/getmanagedassistant.js";
import {
  InterruptAssistantTurnRequest,
  InterruptAssistantTurnSecurity,
} from "../models/operations/interruptassistantturn.js";
import {
  ListAssistantsRequest,
  ListAssistantsSecurity,
} from "../models/operations/listassistants.js";
import {
  SendAssistantMessageRequest,
  SendAssistantMessageSecurity,
} from "../models/operations/sendassistantmessage.js";
import {
  UpdateAssistantRequest,
  UpdateAssistantSecurity,
} from "../models/operations/updateassistant.js";
import {
  UpgradeAssistantIdentityRequest,
  UpgradeAssistantIdentitySecurity,
} from "../models/operations/upgradeassistantidentity.js";
import { unwrapAsync } from "../types/fp.js";

export class Assistants extends ClientSDK {
  /**
   * createAssistant assistants
   *
   * @remarks
   * Create an assistant.
   */
  async create(
    request: CreateAssistantRequest,
    security?: CreateAssistantSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Assistant> {
    return unwrapAsync(assistantsCreate(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * deleteAssistant assistants
   *
   * @remarks
   * Delete an assistant.
   */
  async delete(
    request: DeleteAssistantRequest,
    security?: DeleteAssistantSecurity | undefined,
    options?: RequestOptions,
  ): Promise<void> {
    return unwrapAsync(assistantsDelete(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * ensureManagedAssistant assistants
   *
   * @remarks
   * Get the project's built-in Project Assistant, provisioning it on first access. Idempotent — safe to call on every sidebar open.
   */
  async ensureManaged(
    request?: EnsureManagedAssistantRequest | undefined,
    security?: EnsureManagedAssistantSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Assistant> {
    return unwrapAsync(assistantsEnsureManaged(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getAssistant assistants
   *
   * @remarks
   * Get an assistant by ID.
   */
  async get(
    request: GetAssistantRequest,
    security?: GetAssistantSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Assistant> {
    return unwrapAsync(assistantsGet(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * getManagedAssistant assistants
   *
   * @remarks
   * Get the project's built-in Project Assistant if it exists. Returns 404 when no managed assistant has been provisioned yet — call ensureManagedAssistant to create one.
   */
  async getManaged(
    request?: GetManagedAssistantRequest | undefined,
    security?: GetManagedAssistantSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Assistant> {
    return unwrapAsync(assistantsGetManaged(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * interruptTurn assistants
   *
   * @remarks
   * Stop whatever a conversation is currently generating. Cancels turns still queued on the conversation's thread and interrupts the turn in flight on the assistant runtime, so the reply stops where it is rather than finishing in the background. Idempotent and safe to call when nothing is running: `stopped` is false when the reply had already finished.
   */
  async interruptTurn(
    request: InterruptAssistantTurnRequest,
    security?: InterruptAssistantTurnSecurity | undefined,
    options?: RequestOptions,
  ): Promise<InterruptTurnResult> {
    return unwrapAsync(assistantsInterruptTurn(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * listAssistants assistants
   *
   * @remarks
   * List assistants for the current project.
   */
  async list(
    request?: ListAssistantsRequest | undefined,
    security?: ListAssistantsSecurity | undefined,
    options?: RequestOptions,
  ): Promise<ListAssistantsResult> {
    return unwrapAsync(assistantsList(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * sendMessage assistants
   *
   * @remarks
   * Send a message from the dashboard to an assistant as the calling user. Continue an existing conversation by passing its chat_id (from listChats), or omit chat_id to start a new conversation — the server mints and returns a fresh chat id. The reply is delivered asynchronously; poll the chat service (loadChat) to read it.
   */
  async sendMessage(
    request: SendAssistantMessageRequest,
    security?: SendAssistantMessageSecurity | undefined,
    options?: RequestOptions,
  ): Promise<SendMessageResult> {
    return unwrapAsync(assistantsSendMessage(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * updateAssistant assistants
   *
   * @remarks
   * Update an assistant.
   */
  async update(
    request: UpdateAssistantRequest,
    security?: UpdateAssistantSecurity | undefined,
    options?: RequestOptions,
  ): Promise<Assistant> {
    return unwrapAsync(assistantsUpdate(
      this,
      request,
      security,
      options,
    ));
  }

  /**
   * upgradeAssistantIdentity assistants
   *
   * @remarks
   * Give an existing assistant an agent and per-trigger workload identities. By default a new agent is created with access to every MCP server and skill in the project and to administering this assistant. Passing agent_id points the assistant at an existing agent of the project instead; that agent keeps its policy and gains administration of this assistant. Either way the agent is managed like any other agent afterwards. Existing assistants are never upgraded implicitly; repeating the upgrade is safe.
   */
  async upgradeIdentity(
    request: UpgradeAssistantIdentityRequest,
    security?: UpgradeAssistantIdentitySecurity | undefined,
    options?: RequestOptions,
  ): Promise<Assistant> {
    return unwrapAsync(assistantsUpgradeIdentity(
      this,
      request,
      security,
      options,
    ));
  }
}
